import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './PatientList.css';

const NotesSearch = () => {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;
  const [query, setQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, per_page: 20, total: 0 });
  const [useRange, setUseRange] = useState(false);

  // Debounce query
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const debounceTimer = useRef(null);
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => setDebouncedQuery(query.trim()), 400);
    return () => debounceTimer.current && clearTimeout(debounceTimer.current);
  }, [query]);

  const canSearch = useMemo(() => debouncedQuery.length >= 2, [debouncedQuery]);

  const fetchResults = async (page = 1) => {
    try {
      if (!canSearch) {
        setResults([]);
        setPagination({ current_page: 1, last_page: 1, per_page: 20, total: 0 });
        return;
      }
      setLoading(true);
      setError(null);

            
      const params = new URLSearchParams();
      params.append('query', debouncedQuery);
      params.append('per_page', String(pagination.per_page || 20));
      params.append('page', String(page));
      if (useRange) {
        if (startDate) params.append('start_date', startDate);
        if (endDate) params.append('end_date', endDate);
      }

      const response = await axios.get(`${API_URL}notes/search?${params.toString()}`, {
        headers: { 'Accept': 'application/json' },
      });

      const data = response.data;
      if (!data || !Array.isArray(data.data)) {
        throw new Error('Invalid response from server');
      }

      const normalized = data.data.map((note) => ({
        id: note.id,
        date: note.date,
        type: note.type,
        appointment_note: note.appointment_note,
        description: note.description,
        patient: note.patient || null,
        prescriptions: note.prescriptions || [],
        referrals: note.referrals || [],
      }));

      setResults(normalized);
      setPagination({
        current_page: data.current_page,
        last_page: data.last_page,
        per_page: data.per_page,
        total: data.total,
      });
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message || 'Failed to fetch results');
    } finally {
      setLoading(false);
    }
  };

  // Trigger search on filters change
  useEffect(() => {
    fetchResults(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, startDate, endDate, useRange]);

  const handleToggleRange = (e) => {
    setUseRange(e.target.checked);
    if (!e.target.checked) {
      setStartDate('');
      setEndDate('');
    }
  };

  const gotoPage = (page) => {
    if (page < 1 || page > pagination.last_page) return;
    fetchResults(page);
  };

  const renderDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?/);
      if (m) {
        const year = parseInt(m[1], 10);
        const month = parseInt(m[2], 10);
        const day = parseInt(m[3], 10);
        const hasTime = !!m[4];
        const hourNum = parseInt(m[4] || '0', 10);
        const minute = m[5] ? m[5].padStart(2, '0') : '00';
        const hour12 = ((hourNum % 12) || 12);
        const ampm = hourNum >= 12 ? 'PM' : 'AM';
        const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        const monthLabel = monthNames[Math.max(0, Math.min(11, month - 1))];
        if (hasTime) return `${monthLabel} ${day}, ${year}, ${String(hour12).padStart(2,'0')}:${minute} ${ampm}`;
        return `${monthLabel} ${day}, ${year}`;
      }
      return dateStr;
    } catch (_) {
      return dateStr;
    }
  };

  return (
    <div className="appointments-container">
      <div className="container mt-4">
        <div className="row justify-content-center">
          <div className="col-md-12">
            <div className="card patient-list-card">
              <div className="card-header patient-list-header d-flex justify-content-between align-items-center">
                <h4>Notes Search</h4>
                <div>
                  <button type="button" className="btn create-patient-btn" onClick={() => navigate(-1)}>
                    <i className="fas fa-arrow-left me-2"></i>Back
                  </button>
                </div>
              </div>
              <div className="card-body patient-list-body">

                {/* Search and filters */}
                <div className="search-filters-section">
                  <div className="row g-2 align-items-end">
                    <div className="col-md-6">
                      <label className="form-label small text-muted">Search term</label>
                      <div className="input-group search-input-group">
                        <input
                          type="text"
                          className="form-control search-input"
                          placeholder="Type at least 2 characters..."
                          value={query}
                          onChange={(e) => setQuery(e.target.value)}
                        />
                        <button
                          className="btn search-btn"
                          type="button"
                          onClick={() => fetchResults(1)}
                          disabled={!canSearch || loading}
                          title="Search"
                        >
                          <i className="fas fa-search"></i>
                        </button>
                      </div>
                    </div>
                    <div className="col-12 mt-2">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="useRangeToggle"
                          checked={useRange}
                          onChange={handleToggleRange}
                        />
                        <label className="form-check-label" htmlFor="useRangeToggle">
                          <i className="fas fa-calendar-alt me-2"></i>
                          Use date range filter
                        </label>
                      </div>
                    </div>

                    {useRange && (
                      <>
                        <div className="col-md-3">
                          <label className="form-label small text-muted">From Date</label>
                          <input
                            type="date"
                            className="form-control date-input"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label small text-muted">To Date</label>
                          <input
                            type="date"
                            className="form-control date-input"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                          />
                        </div>
                        {(startDate || endDate) && (
                          <div className="col-md-2">
                            <button className="btn search-btn w-100" type="button" onClick={() => { setStartDate(''); setEndDate(''); }}>
                              <i className="fas fa-times me-1"></i>Clear
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Results */}
                {loading ? (
                  <div className="appointments-loading">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p>Searching notes...</p>
                  </div>
                ) : error ? (
                  <div className="alert alert-danger" role="alert">Error: {error}</div>
                ) : results.length === 0 ? (
                  <div className="no-appointments-state">
                    <div className="d-flex flex-column align-items-center">
                      <i className="fas fa-file-medical-alt no-appointments-icon"></i>
                      <h5 className="no-appointments-title">{canSearch ? 'No matches found' : 'Start by typing a search term'}</h5>
                      <p className="no-appointments-text">
                        {canSearch ? 'Try a different keyword or adjust your date range.' : 'Search across note descriptions, prescriptions, treatments, and referrals.'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="table-responsive patients-table-container">
                    <table className="table table-hover patients-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Patient</th>
                          <th className="d-none d-md-table-cell">Match</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.map((note, index) => (
                          <tr key={note.id}>
                            <td>{(pagination.current_page - 1) * pagination.per_page + index + 1}</td>
                            <td>
                              <div>
                                {note.patient ? (
                                  <Link to={`/control/patient/${note.patient.id}`} className="patient-name-link">
                                    {note.patient.name || 'Unknown Patient'}
                                  </Link>
                                ) : (
                                  <span className="text-muted">Unknown Patient</span>
                                )}
                                <div className="text-muted" style={{ fontSize: '0.65rem', marginTop: '2px' }}>
                                  {note.type ? note.type.charAt(0).toUpperCase() + note.type.slice(1) : ''}
                                </div>
                              </div>
                            </td>
                            <td className="d-none d-md-table-cell">
                              <div className="appointment-description">
                                {note.appointment_note && (
                                  <div><strong>Appointment:</strong> {note.appointment_note}</div>
                                )}
                                {!note.appointment_note && note.description && (
                                  <div dangerouslySetInnerHTML={{ __html: clipHtml(note.description, 200) }} />
                                )}
                                {(!note.appointment_note && !note.description) && (
                                  <span className="text-muted">Prescription/Referral match</span>
                                )}
                              </div>
                            </td>
                            <td>
                              <span className={`status-badge status-pending`}>
                                <i className="fas fa-clock me-1"></i>
                                {renderDate(note.date)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination */}
                {results.length > 0 && (
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div className="text-muted small">
                      Showing {(pagination.current_page - 1) * pagination.per_page + 1}
                      {' - '}
                      {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total}
                    </div>
                    <div className="btn-group" role="group">
                      <button className="btn btn-outline-secondary" onClick={() => gotoPage(1)} disabled={pagination.current_page <= 1}>
                        « First
                      </button>
                      <button className="btn btn-outline-secondary" onClick={() => gotoPage(pagination.current_page - 1)} disabled={pagination.current_page <= 1}>
                        ‹ Prev
                      </button>
                      <button className="btn btn-outline-secondary" onClick={() => gotoPage(pagination.current_page + 1)} disabled={pagination.current_page >= pagination.last_page}>
                        Next ›
                      </button>
                      <button className="btn btn-outline-secondary" onClick={() => gotoPage(pagination.last_page)} disabled={pagination.current_page >= pagination.last_page}>
                        Last »
                      </button>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper to clip inner HTML strings safely (basic)
function clipHtml(html, maxLen = 200) {
  try {
    const text = String(html).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    if (text.length <= maxLen) return text;
    return text.slice(0, maxLen - 1) + '…';
  } catch (_) {
    return '';
  }
}

export default NotesSearch;
